import { FormControl } from '@angular/forms';

/**
 * 字段渲染模式枚舉
 */
export enum FieldRenderMode {
  FORM = 'form',      // 填寫模式 - 可編輯的表單元件
  DISPLAY = 'display', // 顯示模式 - 純顯示，不可編輯
  LIST = 'list'       // 列表模式 - 緊湊型顯示，適用於表格列表
}

/**
 * 字段渲染器配置介面
 */
export interface FieldRendererConfig {
  field: FormFieldConfig;
  value?: any;
  mode: FieldRenderMode;
  formControl?: FormControl;
  readonly?: boolean;
  disabled?: boolean;
  preview?: boolean;
  // 額外配置參數
  currentUserId?: string;
  getuseridlist?: boolean;
  initiatorInfo?: any;
  // 列表模式特有配置
  isListMode?: boolean;
  // 其他共用配置
  [key: string]: any;
}

/**
 * 表單字段配置介面 (從現有的 FormFieldConfig 導入)
 */
export interface FormFieldConfig {
  id: string;
  name: string;
  label: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  value?: any;
  options?: SelectOption[];
  readonly?: boolean;
  disabled?: boolean;
  
  // 字段驗證
  validationRules?: ValidationRule[];
  
  // 條件顯示
  conditionalDisplay?: ConditionalDisplay;
  
  // 階段相關
  stageId?: string;
  
  // 其他配置 (從現有代碼中提取的常用屬性)
  maxFileSize?: number;
  acceptedFormats?: string[];
  displayMode?: string;
  format?: string;
  multiSelect?: boolean;
  maxSelections?: number;
  
  // 動態API相關
  formId?: string;
  labelField?: string;
  valueField?: string;
  enableWhereCondition?: boolean;
  whereSourceComponent?: string;
  whereTargetField?: string;
  whereTargetValue?: string;
  selectcols?: SelectColumn[];
  
  // 日期相關
  dateType?: 'date' | 'datetime';
  startLabel?: string;
  endLabel?: string;
  allowSameDate?: boolean;
  
  // 檔案上傳相關
  fileUploadConfig?: FileUploadConfig;
  
  // 風險評估相關
  config?: any;
  
  // 自動編號相關
  autoNumbering?: AutoNumberingConfig;
  
  // 簽核相關
  approvalFlowId?: string;
  
  // 其他配置
  questionImageUrl?: string;
  questionImageBlobName?: string;
  questionImageAltText?: string;
  conditionalInputs?: any;
  
  // 通用配置
  [key: string]: any;
}

/**
 * 選項介面
 */
export interface SelectOption {
  label: string;
  value: any;
  disabled?: boolean;
  [key: string]: any;
}

/**
 * 選擇列介面
 */
export interface SelectColumn {
  field: string;
  header: string;
  width?: string;
  type?: string;
  [key: string]: any;
}

/**
 * 驗證規則介面
 */
export interface ValidationRule {
  type: 'required' | 'minLength' | 'maxLength' | 'pattern' | 'custom';
  value?: any;
  message?: string;
}

/**
 * 條件顯示介面
 */
export interface ConditionalDisplay {
  dependsOn: string;
  condition: 'equals' | 'notEquals' | 'contains' | 'notContains';
  value: any;
}

/**
 * 檔案上傳配置介面
 */
export interface FileUploadConfig {
  maxFiles?: number;
  maxFileSize?: number;
  allowedTypes?: string[];
  enableDragDrop?: boolean;
  enablePreview?: boolean;
  allowRename?: boolean;
  showFileSize?: boolean;
  showUploadTime?: boolean;
  uploadButtonText?: string;
  dragDropText?: string;
}

/**
 * 自動編號配置介面
 */
export interface AutoNumberingConfig {
  elements: Array<{
    type: 'dropdown' | 'sequence' | 'year' | 'month' | 'text';
    order: number;
    config: any;
  }>;
  separator?: string;
  preview?: string;
  apiKey?: string;
  sequenceConfig?: {
    digits: number;
    startFrom: number;
    apiKey?: string;
  };
}

/**
 * 字段渲染結果介面
 */
export interface FieldRenderResult {
  html?: string;
  value?: any;
  isValid?: boolean;
  errors?: string[];
}

/**
 * 支援的字段類型
 */
export type SupportedFieldType = 
  | 'text-input'
  | 'number-input'
  | 'textarea'
  | 'select'
  | 'radiobutton'
  | 'multi-checkbox'
  | 'datetime'
  | 'date'
  | 'date-range'
  | 'photo-upload'
  | 'avatar-upload'
  | 'file-upload-manager'
  | 'signature'
  | 'location'
  | 'approval-flow'
  | 'dynamic-api-select'
  | 'dynamic-api-table'
  | 'auto-numbering'
  | 'risk-assessment-matrix'
  | 'timestamp'
  | 'department-selector'
  | 'personnel-selector'
  | 'approval-personnel-selector'
  | 'tags'
  | 'file-display'
  | 'video-player'
  | 'audio-recorder'
  | 'meeting-signature-collection'
  | 'fill-in-field'
  | 'rich-text-display'
  | 'subform-relation'
  | 'required-document-upload';